import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Popover,
    Radio,
    FormControlLabel,
    RadioGroup,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import { useAtom } from 'jotai';
import reportsAtoms, {
    fetchInstallStatsReportAtom,
    fetchInstallStatsDownloadReportAtom,
    getActivity
} from '../../../atoms/reports/ReportsAtom';

import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import dashboardAtoms, { fetchPartsAtom } from '../../../atoms/Dashboard/DashboardAtom';
import DateRangePicker from '../../shared/dateRangePicker';
import { roundDown } from '../../../shared/Helper';
import ToggleButtonComponent from '../../../shared/Dashboard/ToogleButton';
import TableComponent from './TableComponent';

const InstallStats = () => {
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredActivities, setFilteredActivities] = useState([]);
    const chartRef = useRef(null);
    const [active, setActive] = useState('map_view');

    const [chartData, setChartData] = useState({
        dates: [],
        counts: [],
        remaining: []
    });
    const [showChart, setShowChart] = useState(true);

    const [dashboardData] = useAtom(dashboardAtoms);
    const [reportsData] = useAtom(reportsAtoms);
    const [, setActivity] = useAtom(getActivity);
    const [, setInstallStats] = useAtom(fetchInstallStatsReportAtom);
    const [, setInstallStatsDownload] = useAtom(fetchInstallStatsDownloadReportAtom);
    const [, setParts] = useAtom(fetchPartsAtom);
    const { constructionList, statsData } = reportsData;
    const { partList } = dashboardData;

    const today = new Date();
    const open = Boolean(anchorEl);

    useEffect(() => {
        initializeData();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (constructionList?.construction) {
            prepareFilteredActivities();
        }
        // eslint-disable-next-line
    }, [constructionList]);

    useEffect(() => {
        prepareFilteredActivities();
        // eslint-disable-next-line
    }, [searchQuery, constructionList]);

    useEffect(() => {
        if (dateRange[0] && dateRange[1] && selectedActivity) {
            fetchStatsData();
        }
        // eslint-disable-next-line
    }, [dateRange, selectedActivity]);

    useEffect(() => {
        if (statsData) {
            prepareChartData();
        }
        // eslint-disable-next-line
    }, [statsData]);

    const initializeData = async () => {
        setLoading(true);
        try {
            await Promise.allSettled([
                setActivity(),
                setParts()
            ]);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStatsData = async () => {
        if (!dateRange[0] || !dateRange[1] || !selectedActivity) return;

        setLoading(true);
        try {
            const formatDate = (date) => {
                const d = new Date(date);
                return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
            };

            await setInstallStats({
                startDate: formatDate(dateRange[0]),
                endDate: formatDate(dateRange[1]),
                actId: selectedActivity
            });
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (chartData.dates.length > 0) {
            setShowChart(false);
            setTimeout(() => {
                setShowChart(true);
            }, 100);
        }
    }, [chartData]);

    const prepareChartData = () => {
        if (!statsData || !Array.isArray(statsData)) return;

        const dates = [];
        const counts = [];
        const remaining = [];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        statsData.forEach(item => {
            const dateParts = item.date.split('/');
            const formattedDate = `${dateParts[1]}-${months[parseInt(dateParts[0]) - 1]}-${dateParts[2]}`;

            dates.push(formattedDate);
            counts.push(item.deployed || 0);
            remaining.push(item.remaining || 0);
        });

        setChartData({ dates, counts, remaining });
    };

    const prepareFilteredActivities = () => {
        if (!constructionList?.construction) return;

        const parentActivities = [];
        const allActivities = [];

        // Get parent activities first
        Object.entries(constructionList?.construction).forEach(([id, activity]) => {
            if (!activity.parent_activity_id) {
                parentActivities.push({ id: parseInt(id), ...activity });
            }
        });

        // Sort parent activities
        parentActivities.sort((a, b) => a.id - b.id);

        // Build hierarchical structure
        parentActivities.forEach(parent => {
            allActivities.push(parent);

            // Find and add child activities for this parent
            Object.entries(constructionList?.construction).forEach(([id, activity]) => {
                if (activity.parent_activity_id === parent.id) {
                    allActivities.push({ id: parseInt(id), ...activity, isChild: true });
                }
            });
        });

        if (searchQuery) {
            setFilteredActivities(
                allActivities.filter(activity =>
                    activity.name.toLowerCase().includes(searchQuery.toLowerCase())
                )
            );
        } else {
            setFilteredActivities(allActivities);
        }
    };

    const handleActivitySelect = (activityId) => {
        setSelectedActivity(activityId);
        setAnchorEl(null);
        setSearchQuery('');
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchQuery('');
    };

    const handleDownload = async () => {
        if (!statsData || !statsData.length) return;

        setDownloadLoading(true);
        try {
            await setInstallStatsDownload();
        } catch (error) {
            console.error(error);
        } finally {
            setDownloadLoading(false);
        }
    };

    const getLinkedPartName = (partId) => {
        if (!partList || !partId) return '';
        const part = partList.find(p => p.id === parseInt(partId));
        return part?.part_name || '';
    };

    const getYAxisConfig = () => {
        // Calculate dynamic ranges for your data
        const activityData = selectedActivity && constructionList?.construction?.[selectedActivity];
        const partName = activityData ? getLinkedPartName(activityData.part_id) : '';
        const maxCount = Math.max(...chartData.counts, 4);  // Minimum of 5 for better scale
        const remainingValues = chartData.remaining;
        const uniqueRemainingValues = [...new Set(remainingValues)];

        // Check if all remaining values are the same
        const isRemainingConstant = uniqueRemainingValues.length === 1;

        let remainingYAxisConfig;

        if (isRemainingConstant) {
            // If values are constant, create a wider scale to show a straight line
            const constantValue = uniqueRemainingValues[0];
            const padding = constantValue * 0.001; // 0.1% padding

            remainingYAxisConfig = {
                opposite: true,
                title: {
                    text: 'Total Remaining',
                    style: {
                        fontSize: '14px',
                        fontWeight: 600
                    }
                },
                labels: {
                    formatter: function (value) {
                        return roundDown(value).toLocaleString();
                    }
                },
                min: constantValue - padding,
                max: constantValue + padding,
                tickAmount: 4, // Show only 3 ticks
                forceNiceScale: false,
                decimalsInFloat: 0
            };
        } else {
            // Normal configuration for varying values
            const minRemaining = Math.min(...remainingValues);
            const maxRemaining = Math.max(...remainingValues);
            const remainingRange = maxRemaining - minRemaining;

            remainingYAxisConfig = {
                opposite: true,
                title: {
                    text: 'Total Remaining',
                    style: {
                        fontSize: '14px',
                        fontWeight: 600
                    }
                },
                labels: {
                    formatter: function (value) {
                        return roundDown(value).toLocaleString();
                    }
                },
                min: minRemaining - (remainingRange * 0.1),
                max: maxRemaining + (remainingRange * 0.1),
                tickAmount: 4,
                forceNiceScale: false,
                decimalsInFloat: 0
            };
        }

        return [{
            title: {
                text: partName ? `${partName} counts` : 'Counts',
                style: {
                    fontSize: '14px',
                    fontWeight: 600
                }
            },
            labels: {
                formatter: function (value) {
                    if (value % 1 === 0) {
                        return value.toString();
                    }
                    return '';
                }
            },
            min: 0,
            max: maxCount,
            tickAmount: Math.min(maxCount, 5),
            forceNiceScale: false,
            decimalsInFloat: 0
        },
            remainingYAxisConfig];
    };

    const getChartOptions = () => {
        return {
            chart: {
                id: 'install-stats-chart',
                height: 450,
                type: 'line',
                toolbar: {
                    show: true,
                    tools: {
                        download: true,
                        selection: false,
                        zoom: false,
                        zoomin: false,
                        zoomout: false,
                        pan: false,
                        reset: false
                    }
                }
            },
            tooltip: {
                enabled: true,
                shared: true,
                followCursor: true,
                intersect: false,
                x: {
                    show: true,
                    format: 'dd MMM yyyy'
                },
                y: {
                    formatter: function (value) {
                        return value;
                    }
                },
                fixed: {
                    enabled: false,
                }
            },
            stroke: {
                width: [0, 4],
                curve: 'smooth'
            },
            dataLabels: {
                enabled: true,
                enabledOnSeries: [1]
            },
            labels: chartData.dates,
            xaxis: {
                type: 'category',
                categories: chartData.dates,
                labels: {
                    rotate: -45,
                    style: {
                        fontSize: '12px'
                    }
                }
            },
            yaxis: getYAxisConfig(),
            colors: ['#66b3ff', '#4CAF50'],
            legend: {
                show: true,
                position: 'bottom',
                fontSize: '14px'
            }
        };
    };

    const getChartSeries = () => {
        const activityData = selectedActivity && constructionList?.construction?.[selectedActivity];
        const partName = activityData ? getLinkedPartName(activityData.part_id) : 'Counts';

        return [{
            name: `${partName} counts`,
            type: 'column',
            data: chartData.counts
        }, {
            name: 'Total Remaining',
            type: 'line',
            data: chartData.remaining
        }];
    };

    return (
        <Box px={4} py={2} mt={1} sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>

            {/* Controls */}
            <Paper sx={{ p: 1, mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box display="flex" gap={2} alignItems="center">
                        <Box >
                            <DateRangePicker
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                                placeholder="Select date range"
                                maxDate={today}
                            />
                        </Box>
                        <Button
                            variant="outlined"
                            endIcon={<ArrowDropDownIcon />}
                            onClick={handleClick}
                            sx={{
                                width: 250,
                                height: '55px',
                                textTransform: 'none',
                                color: selectedActivity ? 'text.primary' : 'text.secondary',
                                justifyContent: 'space-between',
                                px: 2,
                                py: '10px'
                            }}
                        >
                            <Typography noWrap sx={{ maxWidth: 200 }}>
                                {selectedActivity && constructionList?.construction?.[selectedActivity]
                                    ? constructionList?.construction[selectedActivity].name
                                    : 'Select Activity'}
                            </Typography>
                        </Button>
                    </Box>
                    <Box>
                        <ToggleButtonComponent
                            data={["map_view", "table_view"]}
                            active={active} 
                            setActive={setActive}
                        />
                    </Box>
                    <Box>
                        <Button
                            variant="text"
                            startIcon={downloadLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                            onClick={handleDownload}
                            disabled={downloadLoading || !statsData || !statsData.length}
                            sx={{ color: 'text.secondary' }}
                        >
                            Download All
                        </Button>

                    </Box>
                </Box>
            </Paper>

        {active !== "map_view" ?
            <Box>
                <TableComponent />
            </Box> :

            <Box>
                <Box sx={{ flexGrow: 1 }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height={"calc(100vh - 240px)"}>
                            <CircularProgress />
                        </Box>
                    ) : selectedActivity && statsData && statsData.length > 0 ? (
                        <Paper sx={{ p: 3,  height:"calc(100vh - 300px)" }}>
                            {showChart && chartData.dates.length > 0 && (
                                <ReactApexChart
                                    ref={chartRef}
                                    options={getChartOptions()}
                                    series={getChartSeries()}
                                    type="line"
                                    height="100%"
                                    width="100%"
                                />
                            )}
                        </Paper>
                    ) : selectedActivity && statsData && statsData.length === 0 ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="calc(100vh - 240px)"
                        >
                            <ImageOutlinedIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                            <Typography color="text.secondary">No records found</Typography>
                        </Box>
                    ) : (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="calc(100vh - 240px)"
                        >
                            <InsertChartOutlinedIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                            <Typography color="text.secondary">Select a date range and an activity</Typography>
                        </Box>
                    )}
                </Box>
                <Popover
                    id="activity-popover"
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            width: 300,
                            maxHeight: '500px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }
                    }}
                >
                    <Box sx={{ p: 2, pb: 0 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>Select Activity</Typography>

                        <TextField
                            fullWidth
                            placeholder="Search Activity"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchQuery('')}
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 2 }}
                        />
                    </Box>

                    <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, pb: 2 }}>
                        <RadioGroup
                            value={selectedActivity || ''}
                            onChange={(e) => handleActivitySelect(e.target.value)}
                        >
                            {filteredActivities.map((activity) => (
                                <Box
                                    key={activity.id}
                                    sx={{
                                        ml: activity.isChild ? 3 : 0,
                                        my: 0.25
                                    }}
                                >
                                    <FormControlLabel
                                        value={activity.id.toString()}
                                        control={<Radio size="small" />}
                                        disabled={!activity.isChild}
                                        label={
                                            <Typography
                                                sx={{
                                                    fontWeight: activity.isChild ? 'normal' : 'bold',
                                                    fontSize: activity.isChild ? '0.9rem' : '0.95rem',
                                                    color: !activity.isChild ? 'text.disabled' : 'text.primary'
                                                }}
                                            >
                                                {activity.name}
                                            </Typography>
                                        }
                                        sx={{
                                            margin: 0,
                                            '& .MuiFormControlLabel-label': {
                                                marginLeft: 1
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </RadioGroup>
                    </Box>
                </Popover>
            </Box>}

            
        </Box>
    );
};

export default InstallStats;